import { __assign } from "tslib";
import { cacheCookieAccess, COOKIE_ACCESS_DELAY, getCookie } from '../browser/cookie';
import { Observable } from '../tools/observable';
import * as utils from '../tools/utils';
import { timeStampNow } from '../tools/timeUtils';
import { monitor, addMonitoringMessage } from './internalMonitoring';
import { tryOldCookiesMigration } from './oldCookiesMigration';
export var SESSION_COOKIE_NAME = '_dd_s';
export var SESSION_EXPIRATION_DELAY = 15 * utils.ONE_MINUTE;
export var SESSION_TIME_OUT_DELAY = 4 * utils.ONE_HOUR;
export var VISIBILITY_CHECK_DELAY = utils.ONE_MINUTE;
/**
 * Limit access to cookie to avoid performance issues
 */
export function startSessionManagement(options, productKey, computeSessionState) {
    var sessionCookie = cacheCookieAccess(SESSION_COOKIE_NAME, options);
    tryOldCookiesMigration(sessionCookie);
    var renewObservable = new Observable();
    var inMemorySession = retrieveActiveSession(sessionCookie);
    var expandOrRenewSession = utils.throttle(monitor(function () {
        sessionCookie.clearCache();
        var cookieSession = retrieveActiveSession(sessionCookie);
        var retrievedSession = __assign({}, cookieSession);
        var _a = computeSessionState(cookieSession[productKey]), trackingType = _a.trackingType, isTracked = _a.isTracked;
        cookieSession[productKey] = trackingType;
        if (isTracked && !cookieSession.id) {
            cookieSession.id = utils.generateUUID();
            cookieSession.created = String(Date.now());
        }
        // save changes and expand session duration
        persistSession(cookieSession, sessionCookie);
        // If the session id has changed, notify that the session has been renewed
        if (isTracked && inMemorySession.id !== cookieSession.id) {
            inMemorySession = __assign({}, cookieSession);
            renewObservable.notify();
        }
        if (isTracked && inMemorySession[productKey] !== undefined && inMemorySession[productKey] !== trackingType) {
            addMonitoringMessage('session type changed - eors', {
                debug: {
                    product: productKey,
                    inMemorySession: inMemorySession,
                    retrievedSession: retrievedSession,
                    newTrackingType: trackingType,
                    _dd_s: getCookie(SESSION_COOKIE_NAME),
                },
            });
        }
        inMemorySession = __assign({}, cookieSession);
    }), COOKIE_ACCESS_DELAY).throttled;
    var expandSession = function () {
        sessionCookie.clearCache();
        var session = retrieveActiveSession(sessionCookie);
        persistSession(session, sessionCookie);
        if (session.id === inMemorySession.id && session[productKey] !== inMemorySession[productKey]) {
            addMonitoringMessage('session type changed - es', {
                debug: {
                    product: productKey,
                    inMemorySession: inMemorySession,
                    retrievedSession: session,
                    newTrackingType: session[productKey],
                    _dd_s: getCookie(SESSION_COOKIE_NAME),
                },
            });
            inMemorySession = __assign({}, session);
        }
    };
    expandOrRenewSession();
    trackActivity(expandOrRenewSession);
    trackVisibility(expandSession);
    checkCookieConsistency();
    function checkCookieConsistency() {
        var alternateSessionCookie = cacheCookieAccess(SESSION_COOKIE_NAME, options);
        var initTime = timeStampNow();
        setTimeout(function () {
            var sessionCookieCheck = retrieveActiveSession(alternateSessionCookie);
            alternateSessionCookie.clearCache();
            var checkDelay = timeStampNow() - initTime;
            if ((sessionCookieCheck.id !== inMemorySession.id ||
                sessionCookieCheck[productKey] !== inMemorySession[productKey]) &&
                checkDelay < COOKIE_ACCESS_DELAY) {
                addMonitoringMessage('cookie corrupted', {
                    debug: {
                        initTime: initTime,
                        checkDelay: checkDelay,
                        createdDelay: Number(sessionCookieCheck.created) - Number(inMemorySession.created),
                        expireDelay: Number(sessionCookieCheck.expire) - Number(inMemorySession.expire),
                        productKey: productKey,
                        sessionCookieCheck: sessionCookieCheck,
                        inMemorySession: inMemorySession,
                        _dd_s: getCookie(SESSION_COOKIE_NAME),
                    },
                });
            }
        });
        var cookieConsistencyCheckInterval = setInterval(function () {
            var sessionCookieCheck = retrieveActiveSession(alternateSessionCookie);
            alternateSessionCookie.clearCache();
            if (inMemorySession.id === sessionCookieCheck.id &&
                inMemorySession[productKey] !== sessionCookieCheck[productKey]) {
                addMonitoringMessage('session type changed - ccc', {
                    debug: {
                        product: productKey,
                        inMemorySession: inMemorySession,
                        retrievedSession: sessionCookieCheck,
                        newTrackingType: sessionCookieCheck[productKey],
                        _dd_s: getCookie(SESSION_COOKIE_NAME),
                    },
                });
                inMemorySession = __assign({}, sessionCookieCheck);
            }
        }, COOKIE_ACCESS_DELAY);
        stopCallbacks.push(function () { return clearInterval(cookieConsistencyCheckInterval); });
    }
    return {
        getId: function () { return retrieveActiveSession(sessionCookie).id; },
        getTrackingType: function () { return retrieveActiveSession(sessionCookie)[productKey]; },
        getInMemoryTrackingType: function () { return inMemorySession[productKey]; },
        renewObservable: renewObservable,
    };
}
var SESSION_ENTRY_REGEXP = /^([a-z]+)=([a-z0-9-]+)$/;
var SESSION_ENTRY_SEPARATOR = '&';
export function isValidSessionString(sessionString) {
    return (sessionString !== undefined &&
        (sessionString.indexOf(SESSION_ENTRY_SEPARATOR) !== -1 || SESSION_ENTRY_REGEXP.test(sessionString)));
}
function retrieveActiveSession(sessionCookie) {
    var session = retrieveSession(sessionCookie);
    if (isActiveSession(session)) {
        return session;
    }
    clearSession(sessionCookie);
    return {};
}
function isActiveSession(session) {
    // created and expire can be undefined for versions which was not storing them
    // these checks could be removed when older versions will not be available/live anymore
    return ((session.created === undefined || Date.now() - Number(session.created) < SESSION_TIME_OUT_DELAY) &&
        (session.expire === undefined || Date.now() < Number(session.expire)));
}
function retrieveSession(sessionCookie) {
    var sessionString = sessionCookie.get();
    var session = {};
    if (isValidSessionString(sessionString)) {
        sessionString.split(SESSION_ENTRY_SEPARATOR).forEach(function (entry) {
            var matches = SESSION_ENTRY_REGEXP.exec(entry);
            if (matches !== null) {
                var key = matches[1], value = matches[2];
                session[key] = value;
            }
        });
    }
    return session;
}
export function persistSession(session, cookie) {
    if (utils.isEmptyObject(session)) {
        clearSession(cookie);
        return;
    }
    session.expire = String(Date.now() + SESSION_EXPIRATION_DELAY);
    var cookieString = utils
        .objectEntries(session)
        .map(function (_a) {
        var key = _a[0], value = _a[1];
        return key + "=" + value;
    })
        .join(SESSION_ENTRY_SEPARATOR);
    cookie.set(cookieString, SESSION_EXPIRATION_DELAY);
}
function clearSession(cookie) {
    cookie.set('', 0);
}
export function stopSessionManagement() {
    stopCallbacks.forEach(function (e) { return e(); });
    stopCallbacks = [];
}
var stopCallbacks = [];
export function trackActivity(expandOrRenewSession) {
    var stop = utils.addEventListeners(window, ["click" /* CLICK */, "touchstart" /* TOUCH_START */, "keydown" /* KEY_DOWN */, "scroll" /* SCROLL */], expandOrRenewSession, { capture: true, passive: true }).stop;
    stopCallbacks.push(stop);
}
function trackVisibility(expandSession) {
    var expandSessionWhenVisible = monitor(function () {
        if (document.visibilityState === 'visible') {
            expandSession();
        }
    });
    var stop = utils.addEventListener(document, "visibilitychange" /* VISIBILITY_CHANGE */, expandSessionWhenVisible).stop;
    stopCallbacks.push(stop);
    var visibilityCheckInterval = setInterval(expandSessionWhenVisible, VISIBILITY_CHECK_DELAY);
    stopCallbacks.push(function () {
        clearInterval(visibilityCheckInterval);
    });
}
//# sourceMappingURL=sessionManagement.js.map